/**
 * Experience system, client mirror of server/helpers/experience.js
 * (keep the math in sync).
 *
 * XP comes only from completed middleman trades (100 each). Leveling
 * is quadratic and slow: cumulative XP to reach level L = 50*(L-1)*L,
 * so the first trade = level 2, then it stretches out.
 *
 * Levels unlock profile-photo borders (the ladder below). Shops wear
 * their exclusive purple frame automatically, whatever their level.
 */
export const XP_PER_MM_TRADE = 100;

export const xpForLevel = (level) => 50 * (level - 1) * level;

export function levelFromXp(xp) {
    const n = Math.max(0, Number(xp) || 0);
    return Math.max(1, Math.floor((1 + Math.sqrt(1 + n / 12.5)) / 2));
}

// The border ladder. `key` maps to a .pf-<key> class in AvatarFrame.css.
export const BORDERS = [
    { key: 'none', level: 1, name: 'New Trader', blurb: 'Everyone starts here.' },
    { key: 'bronze', level: 2, name: 'Bronze Ring', blurb: 'Your first middleman trade.' },
    { key: 'silver', level: 4, name: 'Silver Ring', blurb: 'A trader people recognize.' },
    { key: 'gold', level: 6, name: 'Gold Ring', blurb: 'The gold standard.' },
    { key: 'radiant', level: 8, name: 'Radiant Gold', blurb: 'It glows. You earned it.' },
    { key: 'mythic', level: 12, name: 'Mythic', blurb: 'The rarest frame on TradeSpot.' },
];

// Special frames (not level-based)
export const SHOP_BORDER = { key: 'shop', name: 'Verified Shop', blurb: 'Worn by verified middleman shops.' };
export const VERIFIED_BORDER = { key: 'verified', name: 'Verified', blurb: 'Email and phone both verified.' };
export const ADMIN_BORDER = { key: 'admin', name: 'Admin', blurb: 'Runs the place.' };

// A user is "verified" when both email and phone check out. Endpoints
// send it precomputed as `verified`; /me sends the raw flags.
export function isVerified(user) {
    if (!user) return false;
    if (user.verified === true || user.verified === 1) return true;
    return !!(user.is_email_verified && user.is_phone_verified);
}

// Highest border unlocked at a level
export function borderForLevel(level) {
    let out = BORDERS[0];
    for (const b of BORDERS) if (level >= b.level) out = b;
    return out;
}

// Every frame this user may wear (drives the Experience page picker).
// Admins have earned nothing and everything: all rings, plus theirs.
export function unlockedBorders(user) {
    if (user?.role === 'admin') {
        return [...BORDERS, VERIFIED_BORDER, SHOP_BORDER, ADMIN_BORDER];
    }
    const level = levelFromXp(user?.experience);
    const out = BORDERS.filter(b => level >= b.level);
    if (isVerified(user)) out.push(VERIFIED_BORDER);
    if (user?.role === 'shop') out.push(SHOP_BORDER);
    return out;
}

// The frame a user actually wears: their saved pick if they still
// qualify for it, otherwise automatic (admin > shop > verified > level)
export function borderForUser(user) {
    if (!user) return BORDERS[0];
    const pick = user.selected_border;
    if (pick) {
        const allowed = unlockedBorders(user).find(b => b.key === pick);
        if (allowed) return allowed;
    }
    if (user.role === 'admin') return ADMIN_BORDER;
    if (user.role === 'shop') return SHOP_BORDER;
    if (isVerified(user)) return VERIFIED_BORDER;
    return borderForLevel(levelFromXp(user.experience));
}
